/**
 * Custom element that shows estimated delivery time and date range.
 * Plain vanilla JS — no module imports, loads directly via <script defer>.
 *
 * On variant change, reads the new `data-delivery-time` from the re-rendered
 * section HTML (event.detail.data.html) — the same pattern used by
 * quantity-total-display.js and product-inventory.js in this theme.
 *
 * Delivery-time format (matches original eleo-express estimateShippingData logic):
 *   "X-Y"  → days range (e.g. "2-5")  → "Lieferzeit X-Y Werktage"
 *   "N"    → weeks (e.g. "6")          → "Lieferzeit ca. N Wochen"
 *   blank  → falls back to data-default-delivery-time
 *
 * @element delivery-time-block
 */
class DeliveryTimeBlock extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    this.#update(this.dataset.deliveryTime);
    this.#attachListeners();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  #attachListeners() {
    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section, dialog');
    // 'variant:update' === ThemeEvents.variantUpdate
    section?.addEventListener('variant:update', this.#onVariantUpdate, { signal });
  }

  /**
   * On variant:update, read the new delivery-time from the re-rendered section HTML.
   * event.detail.data.html is a Document with the newly-rendered section content.
   */
  #onVariantUpdate = (event) => {
    const newEl = event.detail?.data?.html?.querySelector('delivery-time-block');
    if (!newEl) return;
    const newDt = newEl.dataset.deliveryTime ?? '';
    this.#update(newDt);
  };

  /**
   * Updates the delivery label and date range.
   * @param {string | null | undefined} rawDeliveryTime
   */
  #update(rawDeliveryTime) {
    const dt = (rawDeliveryTime || this.dataset.defaultDeliveryTime || '2-5').trim();

    const labelDefault = this.querySelector('.delivery-time__label-default');
    const labelWeeks = this.querySelector('.delivery-time__label-weeks');
    const daysSpan = this.querySelector('.delivery-time__days');
    const weeksSpan = this.querySelector('.delivery-time__weeks');
    const fromSpan = this.querySelector('.delivery-time__from');
    const toSpan = this.querySelector('.delivery-time__to');

    if (!labelDefault || !labelWeeks || !fromSpan || !toSpan) return;

    const [p0 = '0', p1 = '0'] = dt.split('-');

    if (dt.includes('-')) {
      // Days range: "X-Y"
      const min = parseInt(p0, 10);
      const max = parseInt(p1, 10);
      if (daysSpan) daysSpan.textContent = dt;
      labelDefault.removeAttribute('hidden');
      labelWeeks.setAttribute('hidden', '');
      this.#setDates(min, max, fromSpan, toSpan);
    } else {
      // Weeks: "N"
      const weeks = parseInt(p0, 10) || 1;
      if (weeksSpan) weeksSpan.textContent = p0;
      labelWeeks.removeAttribute('hidden');
      labelDefault.setAttribute('hidden', '');
      this.#setDates(weeks * 5, weeks * 5 + 4, fromSpan, toSpan);
    }
  }

  /**
   * @param {number} min
   * @param {number} max
   * @param {Element} fromSpan
   * @param {Element} toSpan
   */
  #setDates(min, max, fromSpan, toSpan) {
    const locale = document.documentElement.lang || 'de';
    const fmt = { day: '2-digit', month: 'long', year: 'numeric' };
    fromSpan.textContent = this.#addBusinessDays(min).toLocaleDateString(locale, fmt);
    toSpan.textContent = this.#addBusinessDays(max).toLocaleDateString(locale, fmt);
  }

  /**
   * Returns a Date `n` business days (Mon-Fri) from today.
   * Identical to the original eleo-express estimateShippingData() algorithm:
   * each weekend day encountered extends the range by one extra calendar day.
   * @param {number} n
   * @returns {Date}
   */
  #addBusinessDays(n) {
    const today = new Date();
    let totalDays = n;
    let result = today;
    for (let i = 1; i <= totalDays; i++) {
      result = new Date(today.getTime() + i * 24 * 60 * 60 * 1000);
      if (result.getDay() === 0 || result.getDay() === 6) totalDays++;
    }
    return result;
  }
}

if (!customElements.get('delivery-time-block')) {
  customElements.define('delivery-time-block', DeliveryTimeBlock);
}
